/**
 * DiscardChangesDialog — lightweight "Discard unsaved changes?" confirmation.
 *
 * Intentionally decoupled from react-query / useMutation. This is a pure
 * local-state confirm; it calls onConfirm or onCancel synchronously.
 */
import { Button } from "@progress/kendo-react-buttons";
import { Dialog } from "@progress/kendo-react-dialogs";
import { AlertTriangle } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";

import "./DiscardChangesDialog.scss";

interface Props {
  open: boolean;
  /** User confirmed they want to discard — caller should close the panel */
  onConfirm: () => void;
  /** User chose to keep editing — caller should keep the panel open */
  onCancel: () => void;
}

const DiscardChangesDialog: React.FC<Props> = ({
  open,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();
  if (!open) return null;

  return (
    <Dialog
      title=" "
      onClose={onCancel}
      width="37.5rem"
      closeIcon
      className="tlx-dialog confirm-dialog discard-changes-dialog"
    >
      <div className="tlx-dialog-discard-changes">
        <div className="tlx-dialog-discard-changes-icon" aria-hidden>
          <AlertTriangle size={28} />
        </div>

        <h1 className="tlx-dialog-discard-changes-title">
          {t("common.discardChanges.title")}
        </h1>
        <p className="tlx-dialog-discard-changes-message">
          {t("common.discardChanges.message")}
        </p>

        <hr className="tlx-dialog-discard-changes-divider" />

        <div className="tlx-dialog-discard-changes-actions">
          <Button
            size="medium"
            type="button"
            onClick={onCancel}
            className="tlx-dialog-discard-changes-btn"
          >
            {t("common.discardChanges.keepEditing")}
          </Button>
          <Button
            size="medium"
            type="button"
            onClick={onConfirm}
            className="tlx-dialog-discard-changes-btn tlx-dialog-discard-changes-btn-danger"
          >
            {t("common.discardChanges.discard")}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default DiscardChangesDialog;
